import { API_ENDPOINTS } from '../api/endpoints';
import pubSub from '../utils/pubsub';

/**
 * One shared subscription-status snapshot for the whole app.
 *
 * Every consumer used to mount its own fetch, so a page carried several
 * identical requests AND several independent copies of the same figures. The
 * copies were the real defect: a post-deduction refetch on one instance left
 * every other one showing a balance the app already knew was out of date, and
 * `KeyContactsTab` fed its contact cards from a different instance than the one
 * it refreshed.
 *
 * Shape: one cached snapshot, one in-flight promise, one subscriber set, read
 * through `useSubscriptionStatus` / `useEnrichmentAccess`, whose signatures do
 * not change. The store is passed in by the caller rather than imported, so
 * store mocks keep working.
 *
 * One load per session, plus one forced load per spend. See
 * `loadSubscriptionStatus` for what that costs and why it is safe.
 */

// The request layer does not always call back. It returns on AbortError, on a
// missing Cognito token (the app redirects to login) and on a gateway 401. With
// the in-flight promise in module scope, an un-settled load would otherwise
// block every future subscriber from ever issuing a real request — and leave
// `await refetch()` hanging. After this long an in-flight load stops being
// joinable AND its waiters are settled via a settle-cap timer (cleared on
// success/error/reset so it does not outlive a settled load).
const SETTLE_CAP_MS = 30_000;

// Monotonic, unlike Date.now(): an NTP correction backwards while a load is
// outstanding would make the elapsed check negative, and a dead load would stay
// joinable for the rest of the session.
const monotonicNow = () => performance.now();

// No `isLoading` field: loading is exactly "the first load has not settled",
// which `hasLoaded` already answers. Writing a separate flag at load start
// published a state change before the response, costing every consumer an extra
// render that the per-mount hook did not have — enough to re-register the MobX
// autoruns in SearchResults.
const EMPTY = { data: null, error: null, hasLoaded: false };

let state = EMPTY;
let inFlight = null;
let inFlightStartedAt = 0;
// Monotonic. Only the newest request may write the snapshot, so a slow earlier
// response cannot land last, and a response in flight across a logout cannot
// write at all.
let sequence = 0;
// Active settle for the current in-flight load. `makeRequest` returns without
// callbacks on missing token, AbortError, and gateway 401 — those paths never
// reach onSuccess/onError/onFinally. Calling this from reset and from the
// settle-cap timer is what keeps `await refetch()` and first-load waiters from
// hanging forever.
let settleCurrent = null;
const subscribers = new Set();

const notify = () => {
  subscribers.forEach((listener) => listener());
};

// `getSnapshot` has to return a stable reference between real changes: React's
// useSyncExternalStore takes no comparator, so a snapshot that allocates on
// every call is an infinite render loop rather than a failed assertion. Build a
// new state object only when a field actually differs.
const setState = (patch) => {
  const next = { ...state, ...patch };
  const changed = Object.keys(next).some((key) => next[key] !== state[key]);
  if (!changed) return;
  state = next;
  notify();
};

export const getSubscriptionStatusSnapshot = () => state;

export const subscribeToSubscriptionStatus = (listener) => {
  subscribers.add(listener);
  return () => {
    subscribers.delete(listener);
  };
};

/**
 * Loads the snapshot and returns a promise resolving with the payload, or null
 * on error, so `await refetch()` still gives callers a fresh pre-action value.
 *
 * @param {Object} store - the root store, from useStore()
 * @param {Object} [options]
 * @param {boolean} [options.force=false] - true for a post-deduction reconcile.
 *   A forced load always issues its own request: joining an open background load
 *   would resolve with the pre-deduction balance and schedule nothing after it.
 *   A forced load is still joinable by later background loads.
 *
 * A background load is a no-op once there is DATA to serve. Every spend in the
 * app reconciles through `force`, so the snapshot stays authoritative for the
 * session without each mount re-reading it — that per-mount read is what this
 * ticket removes. What it costs: a change made outside this tab (a colleague
 * spending a shared balance, an admin editing a limit, a period rolling over)
 * is no longer picked up by navigating, only by the next spend or a reload.
 *
 * Keyed on `data` rather than on `hasLoaded` deliberately. A failed load still
 * sets hasLoaded — that is what stops the counters rendering a skeleton for
 * ever — so short-circuiting on it would mean one transient 500 on the first
 * request of a session left every usage display blank with no way back except a
 * reload. Keyed on data, a failure retries on the next mount, exactly as the
 * per-mount hook did.
 */
export const loadSubscriptionStatus = (store, { force = false } = {}) => {
  if (!force) {
    if (inFlight && monotonicNow() - inFlightStartedAt < SETTLE_CAP_MS) return inFlight;
    if (state.data !== null) return Promise.resolve(state.data);
  }

  const seq = ++sequence;
  setState({ error: null });

  // The snapshot is applied inside settle, synchronously with the store's
  // callback, not in a .then(): the per-mount hook set its state that way, and
  // component tests with a synchronous store mock read the result in the same
  // tick.
  let resolvePending;
  const pending = new Promise((resolve) => {
    resolvePending = resolve;
  });
  inFlight = pending;
  inFlightStartedAt = monotonicNow();

  let settled = false;
  let settleTimer = null;
  const settle = (result) => {
    if (settled) return;
    settled = true;
    if (settleTimer !== null) {
      clearTimeout(settleTimer);
      settleTimer = null;
    }
    if (settleCurrent === settle) settleCurrent = null;
    if (inFlight === pending) inFlight = null;
    if (seq === sequence) {
      setState({
        // An error leaves the last good payload in place, as the per-mount hook
        // did: only a success replaced its data.
        data: result.ok ? result.payload : state.data,
        error: result.ok ? null : result.message || 'Failed to load subscription status',
        hasLoaded: true
      });
    }
    resolvePending(result.ok ? result.payload : null);
  };
  settleCurrent = settle;
  // Cap waiters as well as joiners: without this, a silent makeRequest exit
  // (no token / AbortError / gateway 401) leaves `await refetch()` hanging for
  // the life of the page even after a later load succeeds.
  settleTimer = setTimeout(() => {
    settle({ ok: false, message: null });
  }, SETTLE_CAP_MS);

  // Contained: makeRequest is called outside the promise constructor, so a
  // synchronous throw would otherwise escape into whichever render effect
  // triggered the load AND leave `inFlight` holding a promise that never
  // settles, blocking every consumer until the cap expires.
  try {
    store.makeRequest({
      endpoint: API_ENDPOINTS.GET_SUBSCRIPTION_STATUS,
      onSuccess: (payload) => settle({ ok: true, payload }),
      onError: (message) => settle({ ok: false, message }),
      // Not needed on the happy paths — settle is idempotent and onSuccess /
      // onError already fired — but the store calls whatever it is handed, and
      // several test mocks call onFinally unconditionally. Passing it keeps them
      // from throwing, and settles a mock that calls only onFinally.
      onFinally: () => settle({ ok: false, message: null })
    });
  } catch (requestError) {
    settle({ ok: false, message: requestError?.message || null });
  }

  return pending;
};

/**
 * Acknowledges a period notification (fallback / on-hold) so the BE suppresses
 * it until the next period. Resolves true on success, false on error.
 *
 * After a successful ack, force-refetch rather than writing the flag through
 * locally. An in-flight GET started before the ack was recorded can still
 * settle with `onhold_notify` / `fallback_notify` true; a local write-through
 * would then be clobbered and the modal would re-show. A forced load bumps
 * `sequence`, so that stale settle is discarded and the post-ack payload wins.
 */
export const ackSubscriptionNotification = (store, type) =>
  new Promise((resolve) => {
    store.makeRequest({
      endpoint: API_ENDPOINTS.SUBSCRIPTION_ACK_NOTIFICATION,
      body: { type },
      onSuccess: () => {
        loadSubscriptionStatus(store, { force: true }).then(() => resolve(true));
      },
      onError: () => resolve(false)
    });
  });

// Logout resets stores in place; nothing remounts the tree. Dropping the
// subscribers here would leave every mounted component holding a dead closure,
// rendering the empty snapshot forever — this defect through its own safety net.
// Clear the data and notify, keep the subscribers.
const reset = () => {
  // Resolve any waiter before dropping inFlight — otherwise await refetch()
  // across a logout hangs until the settle-cap timer (or forever if it was
  // cleared by a prior settle that never ran).
  settleCurrent?.({ ok: false, message: null });
  inFlight = null;
  sequence += 1;
  setState(EMPTY);
};

pubSub.subscribe((payload) => {
  if (payload?.event === 'logout') {
    reset();
  }
});

// Tests only. Module state survives between tests in a file, and a request mock
// that never settles would otherwise leave the source in flight for everything
// after it. This is the one variant that drops subscribers.
export const __resetSubscriptionStatusSource = () => {
  reset();
  subscribers.clear();
};
